import {
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  getLayoutByIdApi,
  LayoutResponseType,
} from '@app-store'
import React, { useEffect, useState } from 'react'
import { FieldValues, useFormContext, useWatch } from 'react-hook-form'
import { layoutCache } from '../layoutcache'
import { HybridLayoutDefaultRow } from '../HybridLayoutDefaultRow'
import { isEmpty } from 'lodash'
import {
  buildQuery,
  buildQueryWithParent,
  mergeNestedObjects,
  normalizeLookups,
} from '../../../utils/hybridFormRendererfuntions.utils'
import { SkeletonLoader } from '@ui-kit'
import { HYBRID_LAYOUT_CONSTANTS } from '../../../utils/constants'
import { HybridLayoutAssociatedWatchedRowPropsType } from '../hybridLayout.types'
import { STRINGS } from '../../../utils/strings'
import {
  hasHybridLayoutFormRules,
  HybridFormBehaviorScope,
  triggerHybridFormBehaviorHydrate,
} from '../../../form-behavior/HybridFormBehaviorScope'

export const HybridLayoutAssociatedWatchedRow = ({
  row,
  formUniqueId,
  refetchTimer,
  refetchLogic,
  recordId,
  parentEntityId,
  openAllAccordions,
  task,
  mode,
}: HybridLayoutAssociatedWatchedRowPropsType) => {
  const { setValue, getValues, reset } = useFormContext()
  const fieldDataValue = useWatch({
    name: row?.meta?.associated_field_path + '.id',
  })

  const [layoutDataId, setLayoutDataId] = useState<string>('')

  const layoutId = row?.meta?.layoutId ?? ''
  const entityId = row?.meta?.entityId ?? ''
  const cacheKey = layoutId && entityId ? `${entityId}:${layoutId}` : ''

  const [layoutObject, setLayoutObject] = useState<LayoutResponseType | null>(
    cacheKey && layoutCache.has(cacheKey)
      ? layoutCache.get(cacheKey) || null
      : null
  )
  const [isLoading, setIsLoading] = useState<boolean>(!layoutObject)
  const [error, setError] = useState<boolean>(false)

  const fetchData = async (
    payload: FieldValues,
    parentId: string | null = null
  ) => {
    if (!fieldDataValue && !parentId) return

    let data = {}

    if (parentId) {
      data = buildQueryWithParent(
        parentEntityId, // added

        [
          {
            isAssociated: true,
            addedFields: payload?.layout_data?.added_fields,
            associatedPath: row?.meta?.associated_field_path,
          },
        ],

        {
          parentKey: row?.meta?.associated_field_path,
        },
        payload?.layout_data?.function_parameters
      )
    } else {
      data = buildQuery(entityId, [
        {
          isAssociated: true,
          addedFields: payload?.layout_data?.added_fields,
        },
      ])
    }

    try {
      const response = await getGenericEntityListingAPI(
        data as GetGenericEntityListingApiType,
        parentId ?? fieldDataValue
      )

      if (response) {
        const normalized = normalizeLookups(response)
        const val = getValues()
        const endData = mergeNestedObjects(
          val,
          parentId
            ? normalized?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[
                row?.meta?.associated_field_path ??
                  HYBRID_LAYOUT_CONSTANTS.EXTRA_FIELDS
              ] || {}
            : normalized,
          row?.meta?.associated_field_path
        )
        if (
          !isEmpty(
            endData?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[
              row?.meta?.associated_field_path ??
                HYBRID_LAYOUT_CONSTANTS.EXTRA_FIELDS
            ]
          )
        ) {
          if (
            parentId &&
            normalized?.[
              row?.meta?.associated_field_path ??
                HYBRID_LAYOUT_CONSTANTS.EXTRA_FIELDS
            ].id
          ) {
            setValue(
              `${row?.meta?.associated_field_path}.id`,
              normalized?.[
                row?.meta?.associated_field_path ??
                  HYBRID_LAYOUT_CONSTANTS.EXTRA_FIELDS
              ]?.id
            )
          }
          setValue(
            `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${row?.meta?.associated_field_path}`,
            endData?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[
              row?.meta?.associated_field_path ??
                HYBRID_LAYOUT_CONSTANTS.EXTRA_FIELDS
            ]
          )
          if (
            hasHybridLayoutFormRules(
              (payload as LayoutResponseType) ?? layoutObject
            )
          ) {
            triggerHybridFormBehaviorHydrate((payload ?? layoutObject)?.uid)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    let active = true
    if (!layoutId || !entityId) {
      setLayoutObject(null)
      setIsLoading(false)
      return
    }

    const key = `${entityId}:${layoutId}`
    const fetchLayout = async () => {
      setIsLoading(true)
      setError(false)
      try {
        const response = await getLayoutByIdApi({
          entityId: String(entityId),
          layoutId: String(layoutId),
        })
        if (!active) return
        if (response?.data) {
          const nestedLayout: LayoutResponseType = {
            ...response.data,
            uid: response.data.uid || `layout-${response.data.id ?? layoutId}`,
          }
          layoutCache.set(key, nestedLayout)
          setLayoutObject(nestedLayout)
          // Data fetch runs in a follow-up effect after HybridFormBehaviorScope mounts
        } else {
          setLayoutObject(null)
        }
      } catch (e) {
        if (active) {
          setError(true)
          setLayoutObject(null)
        }
      } finally {
        if (active) setIsLoading(false)
      }
    }

    fetchLayout()
    return () => {
      active = false
    }
  }, [layoutId, entityId, refetchTimer])

  // Fetch after layout mounts with HybridFormBehaviorScope so hydrate finds an evaluator.
  useEffect(() => {
    if (!layoutObject) return
    if (fieldDataValue) {
      setLayoutDataId(fieldDataValue)
      void fetchData(layoutObject, null)
    } else if (recordId) {
      void fetchData(layoutObject, recordId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutObject, fieldDataValue, recordId, refetchTimer])

  if (!layoutId || !entityId) return <div>{STRINGS.NO_LAYOUT_FOUND}</div>
  if (isLoading) return <SkeletonLoader rows={4} columns={4} />
  if (error || isEmpty(layoutObject))
    return <div>{STRINGS.NO_LAYOUT_FOUND}</div>

  return (
    <HybridFormBehaviorScope
      layout={layoutObject}
      formUniqueId={formUniqueId}
      setValue={setValue}
      getValues={getValues}
      reset={reset}
      mode={mode}
    >
      <HybridLayoutDefaultRow
        layoutData={layoutObject}
        recordId={layoutDataId}
        isAssociated
        formUniqueId={formUniqueId}
        associatedObject={{
          associatedPath: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${row?.meta?.associated_field_path}`,
          lookupKeyName: row?.meta?.associated_field_path,
        }}
        refetchTimer={refetchTimer}
        refetchLogic={refetchLogic}
        openAllAccordions={openAllAccordions}
        task={task}
        mode={mode}
      />
    </HybridFormBehaviorScope>
  )
}
